import { useQuery } from "@tanstack/react-query";
import { post } from "@/api/api";

interface BoxplotDataPoint {
  node: string;
  min_data: number;
  p25_data: number;
  median_data: number;
  p75_data: number;
  max_data: number;
  installation_name: string | null;
  location_name: string | null;
  organization_name: string | null;
}

interface SkippedConfig {
  config_id: number;
  config_name: string | null;
  reason: string;
}

interface BoxplotResponse {
  data: BoxplotDataPoint[];
  skipped_configs: SkippedConfig[];
}

export type BoxplotStatistic = 'min_data' | 'p25_data' | 'median_data' | 'p75_data' | 'max_data';

interface BoxplotQueryParams {
  query_type: string;
  sensor_kit_config_ids: number[];
  start: Date | string;
  stop: Date | string;
  has_current_installation?: boolean;
  out_of_bounds_statistic?: BoxplotStatistic;
  order_by?: string;
  order_direction?: string;
}

export interface TransformedBoxplotData {
  id: string;
  name: string;
  location: string;
  min: number;
  p25: number;
  median: number;
  p75: number;
  max: number;
  installation_name: string | null;
  location_name: string | null;
  organization_name: string | null;
}

export interface BoxplotQueryResult {
  data: TransformedBoxplotData[];
  skippedConfigs: SkippedConfig[];
}

const transformBoxplotData = (data: BoxplotDataPoint[]): TransformedBoxplotData[] => {
  return data.map((item) => ({
    id: item.node,
    name: item.node,
    location: "Node",
    min: item.min_data,
    p25: item.p25_data,
    median: item.median_data,
    p75: item.p75_data,
    max: item.max_data,
    installation_name: item.installation_name,
    location_name: item.location_name,
    organization_name: item.organization_name,
  }));
};

export const useBoxplotQuery = (params: BoxplotQueryParams) => {
  return useQuery({
    queryKey: ["boxplot", params],
    queryFn: async (): Promise<BoxplotQueryResult> => {
      const response = await post("queries/boxplot/", params);
      if (!response) return { data: [], skippedConfigs: [] };

      const boxplotResponse = response as BoxplotResponse;
      return {
        data: transformBoxplotData(boxplotResponse.data),
        skippedConfigs: boxplotResponse.skipped_configs,
      };
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};
